import { useMemo } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Info,
  Lightbulb,
  Sparkles,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { GroomingBriefPanel } from "@/components/ado/grooming-brief-panel";
import { ReadinessBadge } from "@/components/ado/readiness-badge";
import { useWorkItem } from "@/hooks/queries/use-work-items";
import { canOn, projectAccessOf, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useGroomingProjects } from "@/hooks/queries/use-grooming";
import { groomingPath } from "@/lib/grooming/paths";
import { sanitizeAdoHtml } from "@/lib/ado-html";
import {
  getAdoPatErrorCode,
  getApiErrorCode,
  keepsItemDetail,
  type ReadinessMissing,
  type WorkItemDetail,
  type WorkItemSectionKey,
} from "@/lib/api/work-items";

const SECTION_LABELS: Record<WorkItemSectionKey, string> = {
  description: "Description",
  acceptanceCriteria: "Acceptance Criteria",
  reproSteps: "Repro Steps",
  systemInfo: "System Info",
};

/** Bugs are written in Repro Steps, so it comes first; stories lead with Description. */
const SECTION_ORDER: Record<WorkItemDetail["kind"], WorkItemSectionKey[]> = {
  bug: ["reproSteps", "systemInfo", "description", "acceptanceCriteria"],
  story: ["description", "acceptanceCriteria", "reproSteps", "systemInfo"],
  other: ["description", "acceptanceCriteria", "reproSteps", "systemInfo"],
};

function SectionCard({ label, html }: { label: string; html: string | undefined }) {
  const clean = useMemo(() => (html ? sanitizeAdoHtml(html) : null), [html]);
  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        {clean ? (
          <div
            className="prose prose-invert prose-sm max-w-none break-words"
            data-testid="ado-section-html"
            dangerouslySetInnerHTML={{ __html: clean }}
          />
        ) : (
          <p className="text-sm italic text-muted-foreground">Not provided.</p>
        )}
      </CardContent>
    </Card>
  );
}

function MissingList({ items, testId }: { items: ReadinessMissing[]; testId: string }) {
  return (
    <ul className="space-y-2" data-testid={testId}>
      {items.map((m) => (
        <li key={m.id} className="flex items-start gap-2 text-sm">
          {m.severity === "critical" ? (
            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-label="Required" />
          ) : (
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-yellow-400" aria-label="Advisory" />
          )}
          <span>
            <span className="font-medium">{m.label}.</span>{" "}
            <span className="text-muted-foreground">{m.missingMessage}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReadinessPanel({ item }: { item: WorkItemDetail }) {
  const { grooming, sprint, readyForGrooming } = item.readiness;
  const groomingIds = new Set(grooming.missing.map((m) => m.id));
  const sprintOnly = sprint.missing.filter((m) => !groomingIds.has(m.id));

  return (
    <Card className="glass-card" data-testid="readiness-panel">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          Readiness
          <ReadinessBadge level="grooming" tier={grooming.tier} />
          <ReadinessBadge level="sprint" tier={sprint.tier} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {grooming.tier === "unsupported" ? (
          <p className="text-sm text-muted-foreground">
            {item.typeName} items are not scored. Only bugs and user stories can be checked against
            the readiness rubric and converted to an Epic Request.
          </p>
        ) : readyForGrooming ? (
          <p className="flex items-center gap-2 text-sm text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            Ready for grooming ({grooming.score}/100). This item can become an Epic Request.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-sm text-red-400">
            <AlertCircle className="h-4 w-4" />
            Not ready for grooming ({grooming.score}/100). Fill in the missing items before it can
            become an Epic Request.
          </p>
        )}

        {grooming.missing.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Missing for grooming
            </h3>
            <MissingList items={grooming.missing} testId="grooming-missing" />
          </div>
        )}

        {sprintOnly.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Missing for sprint (report only)
            </h3>
            <MissingList items={sprintOnly} testId="sprint-missing" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConvertPanel({ item }: { item: WorkItemDetail }) {
  if (item.convertCommand) {
    return (
      <Card className="glass-card" data-testid="convert-panel">
        <CardContent className="pt-6">
          <CommandCopyBox
            label="Convert to Epic Request"
            command={item.convertCommand}
            helperText="Run this slash command in a Claude Code session in your project. It re-checks readiness and creates the Epic Request linked to this item."
          />
        </CardContent>
      </Card>
    );
  }

  if (item.formulatorPath) {
    return (
      <Card className="border-yellow-500/40 bg-yellow-500/5" data-testid="improve-panel">
        <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-yellow-400" />
            This item needs more information before it can become an Epic Request. The ADO Formulator
            opens in SpecTree Toolbox, asks only about what is missing and updates the item in Azure
            DevOps after you approve the changes.
          </p>
          <Button asChild className="shrink-0">
            <Link to={item.formulatorPath}>
              <Sparkles className="mr-2 h-4 w-4" />
              Improve in ADO Formulator
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}

/**
 * One Azure DevOps item: its sections (sanitised), live readiness, and either
 * the `/spectree:ado-to-er <id>` copy command (ready for grooming) or an
 * Improve in ADO Formulator button (it opens the Formulator in SpecTree Toolbox,
 * PI-SPEC-SW-1420) plus the missing items (not ready). Conversion
 * itself happens in Claude Code (D13); this page never converts. The grooming
 * brief card sits under readiness; the brief is written by the in-session
 * `/spectree:ado-groom` command, never by this page (E277 G1).
 */
/**
 * `/ado/items/:project/:id`: My ADO Items item detail. The link at the foot
 * of every posted brief and author-questions comment points here, so a
 * person with `read-backlog` on the project who opens it directly is sent to
 * the item in Grooming (`/grooming/:project/items/:id`, the reading pane;
 * epic PI-SPEC-SW-E292, feature PI-SPEC-SW-1495, UX-PM-17). Everyone else,
 * including ADO-only readers with no Toolbox role (01-roles.md §6.3), and
 * anyone arriving from My ADO Items or the Formulator (router state
 * `keepItemDetail`), sees item detail. The item is not read until the
 * access read settles, so a role holder never sees item detail flash.
 */
export function AdoItemDetailPage() {
  const { project, id } = useParams<{ project: string; id: string }>();
  const location = useLocation();
  const keep = keepsItemDetail(location.state as unknown);
  const { data: access, isLoading: accessLoading } = useToolboxAccess();
  const mayRead = !keep && !accessLoading && Boolean(project && id) && canOn(access, project, "read-backlog");
  // A global admin reads every project's backlog, so for one with no role there the link goes to
  // Grooming only when the project is set up for grooming; otherwise item detail, as before.
  const adminWithoutRole = mayRead && Boolean(access?.isAdmin) && projectAccessOf(access, project) === null;
  const projectsQuery = useGroomingProjects({ enabled: adminWithoutRole });
  const groomingSetUp =
    !adminWithoutRole || (projectsQuery.data ?? []).some((p) => p.project.toLowerCase() === project?.toLowerCase());
  const deciding = adminWithoutRole && projectsQuery.isLoading;
  const toGrooming = mayRead && !deciding && groomingSetUp;
  const { data: item, isLoading: itemLoading, error, refetch } = useWorkItem("ado", project, id, {
    enabled: keep || (!accessLoading && !deciding && !toGrooming),
  });
  const isLoading = itemLoading || (!keep && (accessLoading || deciding));

  const patError = getAdoPatErrorCode(error);
  const notFound = getApiErrorCode(error) === "NOT_FOUND";

  const backLink = (
    <Button variant="ghost" size="sm" asChild>
      <Link to="/ado/items">
        <ArrowLeft className="mr-2 h-4 w-4" />
        My ADO Items
      </Link>
    </Button>
  );

  if (toGrooming && project && id) {
    return <Navigate to={groomingPath(project, `items/${id}`)} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center" role="status">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="sr-only">Loading item</span>
      </div>
    );
  }

  if (patError) {
    return (
      <div className="container mx-auto max-w-3xl space-y-4 px-4 py-6">
        {backLink}
        <AdoPatCallout code={patError} />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="container mx-auto max-w-3xl space-y-4 px-4 py-6">
        {backLink}
        <Card className="border-red-500/40 bg-red-500/5" role="alert">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertCircle className="h-5 w-5 text-red-400" />
              {notFound ? "Work item not found" : "Could not load this work item"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {notFound
                ? `Item ${id ?? ""} does not exist in ${project ?? ""}, or your PAT cannot see it.`
                : error instanceof Error
                  ? error.message
                  : "Unknown error"}
            </p>
            {!notFound && (
              <Button variant="outline" size="sm" onClick={() => { void refetch(); }}>
                Try again
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const sections = SECTION_ORDER[item.kind].filter(
    (key) => item.sections[key] !== undefined || (item.kind === "bug" ? key === "reproSteps" : key === "description")
  );

  return (
    <div className="container mx-auto max-w-4xl space-y-6 px-4 py-6">
      {backLink}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-sm text-muted-foreground">#{item.ref.id}</span>
          <h1 className="text-2xl font-bold">{item.title}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded border border-white/[0.08] px-2 py-0.5">{item.typeName}</span>
          <span className="rounded border border-white/[0.08] px-2 py-0.5">{item.state.name}</span>
          <span className="rounded border border-white/[0.08] px-2 py-0.5">{item.ref.container}</span>
          {item.severity && (
            <span className="rounded border border-white/[0.08] px-2 py-0.5">
              Severity {item.severity.value}
              {item.severity.isDefault ? " (default)" : ""}
            </span>
          )}
          {item.priority !== undefined && (
            <span className="rounded border border-white/[0.08] px-2 py-0.5">Priority {item.priority}</span>
          )}
          {item.estimate !== undefined && (
            <span className="rounded border border-white/[0.08] px-2 py-0.5">{item.estimate} pts</span>
          )}
          <a
            href={item.ref.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            Open in Azure DevOps
          </a>
          {item.linkedEpicRequest && (
            <Link
              to={`/epic-requests/${item.linkedEpicRequest.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-blue-900/30 px-2 py-0.5 text-blue-300 hover:bg-blue-900/50"
              data-testid="linked-epic-request"
            >
              <Lightbulb className="h-3 w-3" />
              {item.linkedEpicRequest.identifier ?? item.linkedEpicRequest.title}
            </Link>
          )}
        </div>
      </div>

      <ReadinessPanel item={item} />
      <GroomingBriefPanel item={item} />
      <ConvertPanel item={item} />

      <div className="space-y-4">
        {sections.map((key) => (
          <SectionCard key={key} label={SECTION_LABELS[key]} html={item.sections[key]?.html} />
        ))}
      </div>
    </div>
  );
}
